import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CssBaseline from '@mui/material/CssBaseline';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { EpisodesApi } from '../../../network';
import { EpisodeAdminModel } from '../../../network/rest';
import ApiErrorHandler from '../../../utils/ApiErrorHandler';
import { translations } from '../../../utils/staticContent';
import AddEntryButton from '../../components/AddEntryButton';
import Modal from '../../components/Modal';
import useModal from '../../components/Modal/useModal';
import Title from '../../components/Title';
import WithAuthentication from '../../components/WithAuthentication';
import ShowsEpisodesForm from './ShowEpisodesForm';

function AdminShowEpisodes() {
    const { isModalOpen, openModal, closeModal } = useModal();

    const [data, setData] = useState<EpisodeAdminModel[]>([]);
    const [dataCount, setDataCount] = useState(0);
    const [controller, setController] = useState({
        page: 0,
        rowsPerPage: 10,
    });
    const [selectedItem, setSelectedItem] = useState<EpisodeAdminModel | undefined>();
    const { showId } = useParams();

    useEffect(() => {
        const getData = async () => {
            try {
                if (!showId) {
                    return;
                }
                const response = await EpisodesApi.getEpisodesAdminApiV1EpisodesAdminGet({
                    showId,
                    offset: controller.page * controller.rowsPerPage,
                    limit: controller.rowsPerPage,
                });
                if (response) {
                    setData(response.episodes);
                    setDataCount(response.total);
                }
            } catch (e) {
                ApiErrorHandler(e);
            }
        };
        if (!isModalOpen) {
            getData();
        }
    }, [controller, isModalOpen, showId]);

    async function deleteItem(id: string) {
        try {
            await EpisodesApi.deleteEpisodeApiV1EpisodesEpisodeDelete({
                id,
            });

            handlePageChange(0);
        } catch (e) {
            ApiErrorHandler(e);
        }
    }

    function handlePageChange(newPage: number) {
        setController({
            ...controller,
            page: newPage,
        });
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setController({
            ...controller,
            rowsPerPage: parseInt(event.target.value, 10),
            page: 0,
        });
    };

    return (
        <WithAuthentication>
            <div className="AdminShowEpisodes">
                <Title>{translations.episodes}</Title>
                <CssBaseline />
                <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 650 }} aria-label="simple table">
                        <TableHead>
                            <TableRow>
                                <TableCell>{translations.episodeNumber}</TableCell>
                                <TableCell>{translations.title}</TableCell>
                                <TableCell>{translations.date}</TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.map((row) => (
                                <TableRow
                                    key={row.id}
                                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                >
                                    <TableCell component="th" scope="row" width={120}>
                                        {row.episodeNumber}
                                    </TableCell>
                                    <TableCell>{row.title}</TableCell>
                                    <TableCell>
                                        {row.originalDate.toLocaleDateString('sl')}
                                    </TableCell>
                                    <TableCell width={120}>
                                        <IconButton
                                            onClick={() => {
                                                setSelectedItem(row);
                                                openModal();
                                            }}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton onClick={() => deleteItem(row.id)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        onPageChange={(e, page) => handlePageChange(page)}
                        page={controller.page}
                        count={dataCount}
                        rowsPerPage={controller.rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </TableContainer>
                <AddEntryButton onClick={openModal} />
                <Modal
                    isOpen={isModalOpen}
                    handleClose={(e, reason) => {
                        closeModal(reason);
                    }}
                    title={selectedItem ? translations.editContent : translations.insertContent}
                >
                    <ShowsEpisodesForm episode={selectedItem} />
                </Modal>
            </div>
        </WithAuthentication>
    );
}

export default AdminShowEpisodes;
